/**
 * Hit testing utilities for finding nearest data points to screen coordinates.
 * Essential for hover/click interactions, tooltips, and crosshair snapping.
 */

import { getPointValue } from "./dataUtils";
import type {
  OhlcvPoint,
  PricePoint,
  ProcessedPricePoint,
  Scale,
} from "~types/types";

// ==================== Types ====================

export interface HitTestOptions {
  xScale: Scale;
  yScale: Scale;
  maxDistanceX?: number;
  maxDistanceY?: number;
}

export interface NearestPointResult {
  point: PricePoint | OhlcvPoint;
  index: number;
  distanceX: number;
  distanceY: number;
  distanceTotal: number;
  screenX: number;
  screenY: number;
}

export interface MultiSeriesHitResult {
  timestamp: number;
  points: Map<string, NearestPointResult | null>;
}

export interface MultiSeriesHitOptions {
  xScale: Scale;
  yScales: Map<string, Scale>;
}

export interface CandlestickHitResult {
  point: OhlcvPoint;
  index: number;
  hitArea: "body" | "wick" | "none";
  screenX: number;
  screenY: number;
}

export interface CandlestickHitOptions {
  xScale: Scale;
  yScale: Scale;
  candleWidth: number;
}

export interface SnapOptions {
  xScale: Scale;
  yScale: Scale;
  snapToX?: boolean;
  snapToY?: boolean;
}

export interface SnapResult {
  screenX: number;
  screenY: number;
  timestamp: number;
  value: number;
  snapped: boolean;
}

export interface VisibleDataResult {
  data: (PricePoint | OhlcvPoint)[];
  startIndex: number;
  endIndex: number;
}

// ==================== Helper Functions ====================

/**
 * Check if a data point is a filled/synthetic point.
 * Filled points are forward-filled to cover small gaps.
 */
function isFilledPoint(point: PricePoint | OhlcvPoint): boolean {
  return (point as ProcessedPricePoint).isFilled === true;
}

/**
 * Find the nearest non-filled point, searching outward from a starting index.
 * Returns null if no non-filled point exists.
 */
function findNearestNonFilledPoint(
  data: (PricePoint | OhlcvPoint)[],
  startIndex: number
): { point: PricePoint | OhlcvPoint; index: number } | null {
  const startPoint = data[startIndex];

  if (startPoint && !isFilledPoint(startPoint)) {
    return { point: startPoint, index: startIndex };
  }

  // Search outward from startIndex (both directions)
  let left = startIndex - 1;
  let right = startIndex + 1;

  while (left >= 0 || right < data.length) {
    // Prefer earlier point (left) to maintain "snap to last real data" behavior
    if (left >= 0) {
      const leftPoint = data[left];

      if (leftPoint && !isFilledPoint(leftPoint)) {
        return { point: leftPoint, index: left };
      }
      left--;
    }

    if (right < data.length) {
      const rightPoint = data[right];

      if (rightPoint && !isFilledPoint(rightPoint)) {
        return { point: rightPoint, index: right };
      }
      right++;
    }
  }

  return null;
}

/**
 * Calculate Euclidean distance between two points.
 */
export function euclideanDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;

  return Math.hypot(dx, dy);
}

/**
 * Calculate Manhattan distance between two points.
 */
export function manhattanDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  return Math.abs(x2 - x1) + Math.abs(y2 - y1);
}

// ==================== Binary Search ====================

/**
 * Binary search to find index of nearest timestamp.
 * Returns index of element with timestamp closest to target.
 */
export function binarySearchNearestTimestamp(
  data: (PricePoint | OhlcvPoint)[],
  targetTimestamp: number
): number {
  if (data.length === 0) return -1;
  if (data.length === 1) return 0;

  let left = 0;
  let right = data.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midPoint = data[mid];

    if (!midPoint) break;

    if (midPoint.timestamp < targetTimestamp) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  if (left > 0) {
    const leftPoint = data[left];
    const prevPoint = data[left - 1];

    if (!leftPoint || !prevPoint) return left;

    const diffLeft = Math.abs(leftPoint.timestamp - targetTimestamp);
    const diffPrev = Math.abs(prevPoint.timestamp - targetTimestamp);

    if (diffPrev < diffLeft) {
      return left - 1;
    }
  }

  return left;
}

/**
 * Find indices of points within a time range.
 */
export function findPointsInRange(
  data: (PricePoint | OhlcvPoint)[],
  startTimestamp: number,
  endTimestamp: number
): { startIndex: number; endIndex: number } {
  if (data.length === 0) {
    return { startIndex: 0, endIndex: 0 };
  }

  let startIndex = 0;
  let left = 0;
  let right = data.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midPoint = data[mid];

    if (!midPoint) break;
    if (midPoint.timestamp < startTimestamp) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  startIndex = left;

  let endIndex = data.length - 1;

  left = 0;
  right = data.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midPoint = data[mid];

    if (!midPoint) break;
    if (midPoint.timestamp <= endTimestamp) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  endIndex = right;

  return {
    startIndex: Math.max(0, startIndex),
    endIndex: Math.min(data.length - 1, endIndex),
  };
}

// ==================== Hit Testing ====================

/**
 * Find the nearest data point to a screen coordinate.
 * Uses binary search for efficiency with sorted timestamp data.
 * Skips filled/synthetic points to snap to real data only.
 */
export function findNearestPoint(
  data: (PricePoint | OhlcvPoint)[],
  screenX: number,
  screenY: number,
  options: HitTestOptions
): NearestPointResult | null {
  const {
    xScale,
    yScale,
    maxDistanceX = Infinity,
    maxDistanceY = Infinity,
  } = options;

  if (data.length === 0) return null;

  const targetTimestamp = xScale.invert(screenX);

  const nearestIndex = binarySearchNearestTimestamp(data, targetTimestamp);

  if (nearestIndex === -1) return null;

  const candidateIndices = [
    nearestIndex - 1,
    nearestIndex,
    nearestIndex + 1,
  ].filter((i) => i >= 0 && i < data.length);

  let closest: NearestPointResult | null = null;
  let minDistance = Infinity;

  for (const index of candidateIndices) {
    const point = data[index];

    // Skip filled/synthetic points
    if (!point || isFilledPoint(point)) continue;

    const pointX = xScale.scale(point.timestamp);
    const pointY = yScale.scale(getPointValue(point));

    const distanceX = Math.abs(pointX - screenX);
    const distanceY = Math.abs(pointY - screenY);

    if (distanceX > maxDistanceX || distanceY > maxDistanceY) continue;

    const distanceTotal = euclideanDistance(screenX, screenY, pointX, pointY);

    if (distanceTotal < minDistance) {
      minDistance = distanceTotal;
      closest = {
        point,
        index,
        distanceX,
        distanceY,
        distanceTotal,
        screenX: pointX,
        screenY: pointY,
      };
    }
  }

  return closest;
}

/**
 * Find nearest point based on X (time) coordinate only.
 * Faster than full hit test when Y position doesn't matter.
 * Uses binary search on timestamps.
 * Handles edge cases for first/last points by clamping to data bounds.
 * Skips filled/synthetic points to snap to real data only.
 */
export function findNearestPointByTime(
  data: (PricePoint | OhlcvPoint)[],
  screenX: number,
  xScale: Scale
): NearestPointResult | null {
  if (data.length === 0) return null;

  const targetTimestamp = xScale.invert(screenX);

  // Get data bounds
  const firstPoint = data[0];
  const lastPoint = data.at(-1);

  if (!firstPoint || !lastPoint) return null;

  // Clamp to data bounds - if cursor is before first point, use first point
  // If cursor is after last point, use last point
  let initialIndex: number;

  if (targetTimestamp <= firstPoint.timestamp) {
    initialIndex = 0;
  } else if (targetTimestamp >= lastPoint.timestamp) {
    initialIndex = data.length - 1;
  } else {
    initialIndex = binarySearchNearestTimestamp(data, targetTimestamp);
  }

  if (initialIndex === -1) return null;

  // Find the nearest non-filled point (skip synthetic gap-filled points)
  const result = findNearestNonFilledPoint(data, initialIndex);

  if (!result) return null;

  const { point, index } = result;
  const pointX = xScale.scale(point.timestamp);

  return {
    point,
    index,
    distanceX: Math.abs(pointX - screenX),
    distanceY: 0,
    distanceTotal: Math.abs(pointX - screenX),
    screenX: pointX,
    screenY: 0,
  };
}

/**
 * Find nearest points across multiple series at a given X position.
 * All series snap to the same timestamp for comparison mode.
 */
function findCommonTimestamp(
  seriesData: Map<string, (PricePoint | OhlcvPoint)[]>,
  targetTimestamp: number,
  points: Map<string, NearestPointResult | null>
): number {
  let commonTimestamp = targetTimestamp;
  let closestDistance = Infinity;

  for (const [seriesId, data] of Array.from(seriesData.entries())) {
    if (data.length === 0) {
      points.set(seriesId, null);
      continue;
    }

    const index = binarySearchNearestTimestamp(data, targetTimestamp);

    if (index === -1) {
      points.set(seriesId, null);
      continue;
    }

    const point = data[index];

    if (!point) {
      points.set(seriesId, null);
      continue;
    }

    const distance = Math.abs(point.timestamp - targetTimestamp);

    if (distance < closestDistance) {
      closestDistance = distance;
      commonTimestamp = point.timestamp;
    }
  }

  return commonTimestamp;
}

export function findNearestPointsMultiSeries(
  seriesData: Map<string, (PricePoint | OhlcvPoint)[]>,
  screenX: number,
  screenY: number,
  options: MultiSeriesHitOptions
): MultiSeriesHitResult {
  const { xScale, yScales } = options;
  const targetTimestamp = xScale.invert(screenX);
  const points = new Map<string, NearestPointResult | null>();

  const commonTimestamp = findCommonTimestamp(
    seriesData,
    targetTimestamp,
    points
  );

  for (const [seriesId, data] of Array.from(seriesData.entries())) {
    if (data.length === 0) continue;

    const index = binarySearchNearestTimestamp(data, commonTimestamp);

    if (index === -1) continue;

    const point = data[index];

    if (!point) continue;

    const yScale = yScales.get(seriesId);

    if (!yScale) continue;

    const pointX = xScale.scale(point.timestamp);
    const pointY = yScale.scale(getPointValue(point));

    points.set(seriesId, {
      point,
      index,
      distanceX: Math.abs(pointX - screenX),
      distanceY: Math.abs(pointY - screenY),
      distanceTotal: euclideanDistance(screenX, screenY, pointX, pointY),
      screenX: pointX,
      screenY: pointY,
    });
  }

  return { timestamp: commonTimestamp, points };
}

// ==================== Candlestick Hit Testing ====================

/**
 * Test if screen coordinates hit a candlestick.
 */
export function hitTestCandlestick(
  data: OhlcvPoint[],
  screenX: number,
  screenY: number,
  options: CandlestickHitOptions
): CandlestickHitResult | null {
  const { xScale, yScale, candleWidth } = options;

  if (data.length === 0) return null;

  const targetTimestamp = xScale.invert(screenX);
  const index = binarySearchNearestTimestamp(data, targetTimestamp);

  if (index === -1) return null;

  const point = data[index];

  if (!point) return null;

  const pointX = xScale.scale(point.timestamp);
  const openY = yScale.scale(point.open);
  const closeY = yScale.scale(point.close);
  const highY = yScale.scale(point.high);
  const lowY = yScale.scale(point.low);

  const halfWidth = candleWidth / 2;
  const wickWidth = 2;

  if (screenX < pointX - halfWidth || screenX > pointX + halfWidth) {
    return null;
  }

  let hitArea: "body" | "wick" | "none" = "none";

  const bodyTop = Math.min(openY, closeY);
  const bodyBottom = Math.max(openY, closeY);

  if (
    screenX >= pointX - halfWidth &&
    screenX <= pointX + halfWidth &&
    screenY >= bodyTop &&
    screenY <= bodyBottom
  ) {
    hitArea = "body";
  } else if (
    screenX >= pointX - wickWidth &&
    screenX <= pointX + wickWidth &&
    screenY >= highY &&
    screenY <= lowY
  ) {
    hitArea = "wick";
  }

  if (hitArea === "none") return null;

  return {
    point,
    index,
    hitArea,
    screenX: pointX,
    screenY,
  };
}

// ==================== Snapping ====================

/**
 * Snap screen coordinates to nearest data point.
 */
export function snapToDataPoint(
  data: (PricePoint | OhlcvPoint)[],
  screenX: number,
  screenY: number,
  options: SnapOptions
): SnapResult {
  const { xScale, yScale, snapToX = true, snapToY = false } = options;

  if (data.length === 0 || !snapToX) {
    const timestamp = xScale.invert(screenX);
    const value = yScale.invert(screenY);

    return {
      screenX,
      screenY,
      timestamp,
      value,
      snapped: false,
    };
  }

  const targetTimestamp = xScale.invert(screenX);
  const index = binarySearchNearestTimestamp(data, targetTimestamp);

  if (index === -1) {
    return {
      screenX,
      screenY,
      timestamp: targetTimestamp,
      value: yScale.invert(screenY),
      snapped: false,
    };
  }

  const point = data[index];

  if (!point) {
    return {
      screenX,
      screenY,
      timestamp: targetTimestamp,
      value: yScale.invert(screenY),
      snapped: false,
    };
  }

  const snappedX = xScale.scale(point.timestamp);
  const snappedY = snapToY ? yScale.scale(getPointValue(point)) : screenY;

  return {
    screenX: snappedX,
    screenY: snappedY,
    timestamp: point.timestamp,
    value: snapToY ? getPointValue(point) : yScale.invert(screenY),
    snapped: true,
  };
}

// ==================== Visible Data ====================

/**
 * Get data points visible in current viewport.
 * @param data - Full data array
 * @param viewport - Visible time range
 * @param padding - Extra points to include before/after viewport (default: 0)
 *                  Use padding=1 for path rendering to ensure smooth lines at edges
 */
export function getVisibleData(
  data: (PricePoint | OhlcvPoint)[],
  viewport: { start: number; end: number },
  padding = 0
): VisibleDataResult {
  if (data.length === 0) {
    return { data: [], startIndex: 0, endIndex: 0 };
  }

  const { startIndex: rawStart, endIndex: rawEnd } = findPointsInRange(
    data,
    viewport.start,
    viewport.end
  );

  const startIndex = Math.max(0, rawStart - padding);
  const endIndex = Math.min(data.length - 1, rawEnd + padding);

  return {
    data: data.slice(startIndex, endIndex + 1),
    startIndex,
    endIndex,
  };
}
